/**
 * `decideSkip` — the ONE answer to "may this cell skip its rebind?".
 *
 * ## What this replaces
 *
 * The question used to be decided in three places, and the three DOM axes were
 * re-implemented twice more:
 *
 *   1. `RenderCursor.shouldRebind` — epoch gate, then the LR6 version compare.
 *   2. `diffCellNode` → `classifyCellDirectives` — the scene's stamps, emitted
 *      as a `skip` directive.
 *   3. the binder's `canSkip` closure — `rid` · displayed column · displayed
 *      height · the cursor, re-conjoined by hand;
 *   3'. `honorableOnDom` in the referee pre-pass — the SAME three DOM axes,
 *      copied verbatim, whose refusals were counted as `skipsDowngraded`;
 *   3''. `applyPlanFullRebind`'s fallback, which re-authorised a skip through
 *      the raw `RenderCursor.shouldSkip` — BELOW the applier and therefore
 *      around the structural-epoch gate that `render-cursor.ts` documents as
 *      living only in `shouldRebind` (sdk-0's latent finding C1).
 *
 * They agreed only by hand-maintained coincidence, and each copy had to be
 * updated whenever an axis moved.
 *
 * ## Why the scene cannot simply be told
 *
 * The three DOM axes are out-of-band state: `SheetColumn.onRecycle` drops `rid`
 * and zeroes a pooled column's heights, and `scrubPreviousSheetVisualState`
 * binds every rendered cell to height 0 on a sheet navigation — both on the
 * contract that the NEXT bind pass restores real values. No stamp in the scene
 * moves when that happens, so no amount of teaching the scene diff can make it
 * see them. The fix is not to make the scene see the DOM; it is to have the
 * host — the one layer that CAN see it — evaluate the axes once and hand them
 * across as a value, and to decide in exactly one place.
 *
 * ## The shape
 *
 * `SkipEligibility` is three independent verdicts about the same cell, each
 * from the layer entitled to it. `decideSkip` is their conjunction, plus a
 * NAME for the first term that refused — which is what retires the old
 * `skipsDowngraded` counter: a `because: "dom-unhonorable"` verdict is the
 * same fact with a name, and it now sits beside the other two causes instead
 * of being the only one anybody counted.
 *
 * Pure and DOM-free (Deno-loadable), so the truth table is a headless test
 * rather than a driver run.
 */

/**
 * The three terms a skip must satisfy, each supplied by the layer that owns it.
 * Every term is `true` = "this axis is content with a skip"; a skip needs all
 * three.
 */
export interface SkipEligibility {
  /**
   * The scene's verdict: the classifier emitted `skip` for this cell
   * because the row's stamps did not move (values + attr versions, span mode,
   * layout, binding). False for `rebind` / `resize` / `span`, and for a cell
   * the classifier never spoke about.
   */
  readonly sceneUnchanged: boolean;
  /**
   * LR6 + the structural epoch — the ledger's verdict, exactly as the render
   * cursor reports it (`shouldRebind(id) === false`). The epoch gate lives only
   * inside `shouldRebind`, so this term is the ONLY way it reaches a decision;
   * nothing downstream may substitute the raw `decide` / `shouldSkip` pair for
   * it. `false` whenever there is no cursor at all (a legacy / standalone sheet
   * with no ledger) — the conservative default is always "repaint".
   */
  readonly ledgerUnchanged: boolean;
  /**
   * The three out-of-band DOM axes, evaluated ONCE by the host: the occupant's
   * `rid` still records a completed bind to this row · the occupant already
   * displays this column · the occupant already displays the plan's height.
   * None of the three is visible to the scene or to the ledger.
   */
  readonly domHonorable: boolean;
}

/** Which term refused, when `decideSkip` returns `rebind`. */
export type SkipRebindCause = "scene" | "ledger" | "dom-unhonorable";

/**
 * The single decider's answer. `rebind` names the FIRST term that refused, in
 * the declaration order of {@link SkipEligibility} — scene, then ledger, then
 * DOM — so a census of verdicts attributes each refusal to one cause and the
 * buckets sum to the number of refusals.
 */
export type SkipVerdict =
  | { readonly kind: "skip" }
  | { readonly kind: "rebind"; readonly because: SkipRebindCause };

export namespace SkipVerdict {
  /** Every cause, in the order `decideSkip` tests them. */
  export const CAUSES: readonly SkipRebindCause[] = [
    "scene",
    "ledger",
    "dom-unhonorable",
  ];

  /** A per-cause census of refusals — the `rebindBecause` breadcrumb payload. */
  export type CauseCounts = Record<SkipRebindCause, number>;

  /** A zeroed census; the caller increments `counts[verdict.because]`. */
  export function emptyCauseCounts(): CauseCounts {
    return { scene: 0, ledger: 0, "dom-unhonorable": 0 };
  }
}

/**
 * Skip ⟺ all three terms agree. Otherwise rebind, naming the first term that
 * refused (scene → ledger → DOM).
 *
 * Flip-neutral by construction: this is the same conjunction whether
 * the render flags' `live()` is true or false. With LR6 dark the cursor reports
 * "rebind" for every row, so `ledgerUnchanged` is false and nothing skips —
 * exactly the behavior the four hand-written copies produced.
 */
export function decideSkip(eligibility: SkipEligibility): SkipVerdict {
  if (!eligibility.sceneUnchanged) return { kind: "rebind", because: "scene" };
  if (!eligibility.ledgerUnchanged) return { kind: "rebind", because: "ledger" };
  if (!eligibility.domHonorable) {
    return { kind: "rebind", because: "dom-unhonorable" };
  }
  return { kind: "skip" };
}
